import React, { createContext, useReducer } from 'react'
import { Switch, useParams, Route, useRouteMatch } from 'react-router'
import { SurgicalOrderModel } from 'domain/entities/surgical-order-model'
import AmbulatoryAuthorization from 'presentation/doctor/pages/AmbulatoryAuthorization'
import AmbulatoryOrdersStatusList from 'presentation/doctor/pages/AmbulatoryOrdersStatusList'
import AmbulatoryOrderDetails from 'presentation/doctor/pages/AmbulatoryOrderDetails'
import { Pagination } from 'service/protocols/api/api-client'
import { AmbulatoryOrdersSearchModel } from 'domain/entities/search-ambulatory-orders-model'
import AmbulatoryCategories from 'presentation/doctor/pages/AmbulatoryCategories'
import AmbulatorySolicitation from 'presentation/doctor/pages/AmbulatorySolicitation'
import AmbulatoryPatientInfo from 'presentation/doctor/pages/AmbulatoryPatientInfo'
import AmbulatoryInsuranceStep from 'presentation/doctor/pages/AmbulatoryInsuranceStep'
import AmbulatoryUnitStep from 'presentation/doctor/pages/AmbulatoryUnitStep'
import AmbulatoryProcedures from 'presentation/doctor/pages/AmbulatoryProcedures'
import AmbulatoryCids from 'presentation/doctor/pages/AmbulatoryCids'
import AmbulatoryMedication from 'presentation/doctor/pages/AmbulatoryMedication'
import AmbulatoryMaterials from 'presentation/doctor/pages/AmbulatoryMaterials'
import AmbulatoryObservations from 'presentation/doctor/pages/AmbulatoryObservations'
import AmbulatoryDocuments from 'presentation/doctor/pages/AmbulatoryDocuments'
import AmbulatoryResume from 'presentation/doctor/pages/AmbulatoryResume'
import { AmbulatoryDoctorDocument } from 'common/enum/ambulatory-doctor-document'

export const AmbulatoryAuthorizationDashboardContext = createContext({})

type Types =
  | 'RESET_AMBULATORY_STATE'
  | 'PENDENCY_COUNT'
  | 'PENDENCY_COUNT_FILTERED'
  | 'STATUS_COUNT'
  | 'SCHEDULINGS_COUNT'
  | 'AMBULATORY_ORDERS_LIST'
  | 'SEARCH_AMBULATORY_ORDERS_LIST'
  | 'AMBULATORY_ORDERS_LIST_STATUS'
  | 'PAGINATION'
  | 'DOCTOR_INFO'
  | 'PATIENT_INFO'
  | 'HEALTH_INSURANCE_INFO'
  | 'UNIT_INFO'
  | 'PROCEDURES'
  | 'CIDS'
  | 'MEDICATIONS'
  | 'MATERIALS'
  | 'OBSERVATIONS'
  | 'DOCUMENTS'

type Payload = any

export type Actions = {
  payload: Payload
  type: Types
  clearList?: boolean
}

export type ContextType = {
  dispatch: (params: Actions) => void
  state: {
    statusCount: {
      AUTHORIZED: number
      CANCELED: number
      DONE: number
      HOSPITAL_ANALYSIS: number
      ANALYSIS_INSURANCE: number
      DENIED: number
    }
  }
}

const initialState = {
  statusCount: {
    AUTHORIZED: 0,
    CANCELED: 0,
    DONE: 0,
    ORDER_OPEN: 0,
    HOSPITAL_ANALYSIS: 0,
    ANALYSIS_INSURANCE: 0,
    DENIED: 0
  },
  ambulatoryOrdersList: [] as SurgicalOrderModel[],
  searchAmbulatoryOrdersList: [] as AmbulatoryOrdersSearchModel[],
  pageInfo: {} as Pagination,

  doctor: {
    doctor_id: null,
    name: ''
  },

  patient: {
    patient_id: null,
    name: '',
    cpf: '',
    birthday: '',
    email: '',
    phone: '',
    landline: '',
    gender: '',
    marital_status: ''
  },

  health_insurance_info: {
    health_insurance_code: '',
    health_insurance_name: '',
    health_plan_code: '',
    health_plan_name: '',
    health_sub_plan_code: '',
    health_sub_plan_name: '',
    valid_thru: '',
    health_card: '',
    company_name: ''
  },

  unit: {} as Unit,
  procedures: [] as Procedure[],
  cids: [] as Cid[],
  medications: [] as Medication[],
  materials: [] as Material[],

  observation: '',

  documents: [] as DocumentFile[]
}

const reducer = (state: any, action: Actions) => {
  switch (action.type) {
    case 'RESET_AMBULATORY_STATE':
      return { ...initialState }

    case 'AMBULATORY_ORDERS_LIST':
      if (action.clearList) {
        return { ...state, ambulatoryOrdersList: action.payload }
      } else {
        return {
          ...state,
          ambulatoryOrdersList: [
            ...state.ambulatoryOrdersList,
            ...action.payload
          ]
        }
      }
    case 'AMBULATORY_ORDERS_LIST_STATUS':
      return {
        ...state,
        ambulatoryOrdersList: [...action.payload]
      }

    case 'SEARCH_AMBULATORY_ORDERS_LIST':
      return {
        ...state,
        ambulatoryOrdersList: [...action.payload]
      }

    case 'STATUS_COUNT':
      return {
        ...state,
        statusCount: action.payload
      }

    case 'PAGINATION':
      return { ...state, pageInfo: action.payload }

    case 'DOCTOR_INFO':
      return {
        ...state,
        doctor: action.payload
      }

    case 'PATIENT_INFO':
      return {
        ...state,
        patient: action.payload
      }

    case 'HEALTH_INSURANCE_INFO':
      return {
        ...state,
        health_insurance_info: action.payload
      }

    case 'UNIT_INFO':
      return { ...state, unit: action.payload }

    case 'PROCEDURES':
      return { ...state, procedures: action.payload }

    case 'CIDS':
      return { ...state, cids: action.payload }

    case 'MEDICATIONS':
      return { ...state, medications: action.payload }

    case 'MATERIALS':
      return { ...state, materials: action.payload }

    case 'OBSERVATIONS':
      return { ...state, observation: action.payload }

    case 'DOCUMENTS':
      return { ...state, documents: action.payload }

    default:
      return state
  }
}

export default function makeAmbulatoryAuthorizationPage() {
  const { path } = useRouteMatch()
  const [state, dispatch] = useReducer(reducer, initialState)

  return (
    <>
      <Switch>
        <AmbulatoryAuthorizationDashboardContext.Provider
          value={{ state, dispatch }}
        >
          <Route path={`${path}/:page`} component={Page} />
        </AmbulatoryAuthorizationDashboardContext.Provider>
      </Switch>
    </>
  )
}

function Page() {
  const { page } = useParams<{ page: string }>()
  switch (page) {
    case 'solicitacao':
      return <AmbulatorySolicitation />
    case 'categorias':
      return <AmbulatoryCategories />
    case 'dados-do-paciente':
      return <AmbulatoryPatientInfo />
    case 'convenio':
      return <AmbulatoryInsuranceStep />
    case 'unidade':
      return <AmbulatoryUnitStep />
    case 'procedimentos':
      return <AmbulatoryProcedures />
    case 'cids':
      return <AmbulatoryCids />
    case 'medicacao':
      return <AmbulatoryMedication />
    case 'materiais':
      return <AmbulatoryMaterials />
    case 'observacoes':
      return <AmbulatoryObservations />
    case 'documentos':
      return <AmbulatoryDocuments />
    case 'resumo':
      return <AmbulatoryResume />
    case 'dashboard':
      return <AmbulatoryAuthorization />
    case 'pedidos':
      return <AmbulatoryOrdersStatusList />
    case 'detalhes':
      return <AmbulatoryOrderDetails />
    default:
      return <AmbulatoryAuthorization />
  }
}

export type Unit = {
  hospital_id: number
  friendly_name: string
  name: string
}

export type Procedure = {
  name: string
  code: number
  quantity: number | string
  isMain: boolean
}

export type Cid = {
  name: string
  code: number
  isMain: boolean
}

export type Medication = {
  name: string
  code: number
  quantity: number | string
  routeOfAdministration: string
  dosage: string
  isMain: boolean
}

export type Material = {
  name: string
  quantity: number | string
  isMain: boolean
}

export type DocumentFile = {
  type: AmbulatoryDoctorDocument
  label: string
  document_id?: string
  filename?: string
  file?: File
  status?: string
  required?: boolean
  maxFileSizeInMB?: number
}
